import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiRequest from "./AxiosConfiguration";

const initialState = {
  list: [],
  loading: false,
  error: null,
  selectedMapping: null,
  facilities: [],
  employees: [],
  shifts: [],
  holidays: [],
  dropdownLoading: false,
  availableEmployees: [],
  employeesLoading: false,
  bookedShiftIds: [],
  bookedShiftsLoading: false,
  // Stats data
  stats: {
    totalShifts: 0,
    totalEmployees: 0,
    totalFacilities: 0,
    unassignedAssociates: 0,
  },
};

// Fetch dropdown data (facilities, employees, shifts)
export const fetchDropdownData = createAsyncThunk(
  "shiftMapping/fetchDropdownData",
  async ({ companyId, term = "SHIFT", mappingId = 0 }) => {
    const response = await apiRequest(
      "GET",
      `AssociateShiftMapping/GetDropDownForAssociateShiftMapping?AssociateShiftMappingId=${mappingId}&companyID=${companyId}&term=${term}`,
    );
    return { data: response.data, term, mappingId };
  },
);

// Fetch available employees for a date+facility+shift combination
export const fetchAvailableEmployees = createAsyncThunk(
  "shiftMapping/fetchAvailableEmployees",
  async ({ companyId, facilityId, shiftId, fromDate, toDate, excludeId = 0 }) => {
    const response = await apiRequest(
      "GET",
      `AssociateShiftMapping/GetAvailableEmployeesForShiftMapping?CompanyId=${companyId}&FacilityId=${facilityId}&ShiftId=${shiftId}&FromDate=${fromDate}&ToDate=${toDate}&ExcludeMappingId=${excludeId}`,
    );
    return Array.isArray(response.data) ? response.data : [];
  },
);

// Fetch booked shifts for a facility + date range combination
export const fetchBookedShifts = createAsyncThunk(
  "shiftMapping/fetchBookedShifts",
  async ({ companyId, facilityId, fromDate, toDate, excludeId = 0 }) => {
    const response = await apiRequest(
      "GET",
      `AssociateShiftMapping/GetBookedShiftsForFacility?CompanyId=${companyId}&FacilityId=${facilityId}&FromDate=${fromDate}&ToDate=${toDate}&ExcludeMappingId=${excludeId}`,
    );
    return Array.isArray(response.data) ? response.data.map((r) => r.ShiftId) : [];
  },
);

// Fetch shift mapping list
export const fetchMappingList = createAsyncThunk(
  "shiftMapping/fetchMappingList",
  async (companyId) => {
    const response = await apiRequest(
      "GET",
      `AssociateShiftMapping/GetAssociateShiftMappingList?CompanyId=${companyId}`,
    );
    return response.data;
  },
);

// Create or Update shift mapping
export const createUpdateShiftMapping = createAsyncThunk(
  "shiftMapping/createUpdateShiftMapping",
  async ({ shiftData, companyId }, { rejectWithValue }) => {
    try {
      const response = await apiRequest(
        "POST",
        `AssociateShiftMapping/CreateUpdateShiftMapping`,
        shiftData,
        true,
        false,
        false,
        false,
        shiftData.AssociateShiftMappingId > 0,
      );
      
      // Log the full response to debug
      console.log("API Full Response:", response);
      
      // Access the actual data from the response
      const responseData = response.data || response;

        if (response?.isOffline || responseData?.isOffline) {
        return rejectWithValue({
          success: false,
          isOffline: true,
          message: "No internet connection",
        });
      }
      
      // Check for conflict
      if (responseData.success === false) {
        // Return as fulfilled but with conflict info
        return {
          success: false,
          message: responseData.message || "Conflict exists",
          conflictDates: responseData.conflictDates || null,
          result: responseData.result || -1
        };
      }
      
      // Success case
      return {
        success: true,
        id: responseData.id || responseData,
        data: responseData
      };
      
    } catch (error) {
      return rejectWithValue({
        success: false,
        message: error.message || "Failed to save shift mapping"
      });
    }
  },
);

// Fetch all dropdown data at once for stats
export const fetchAllDropdownData = createAsyncThunk(
  "shiftMapping/fetchAllDropdownData",
  async ({ companyId }) => {
    const [facilitiesRes, employeesRes, shiftsRes] = await Promise.all([
      apiRequest("GET", `AssociateShiftMapping/GetDropDownForAssociateShiftMapping?AssociateShiftMappingId=0&companyID=${companyId}&term=FACILITIES`),
      apiRequest("GET", `AssociateShiftMapping/GetDropDownForAssociateShiftMapping?AssociateShiftMappingId=0&companyID=${companyId}&term=EMPLOYEE`),
      apiRequest("GET", `AssociateShiftMapping/GetDropDownForAssociateShiftMapping?AssociateShiftMappingId=0&companyID=${companyId}&term=SHIFT`),
    ]);

    return {
      facilities: facilitiesRes.data || [],
      employees: employeesRes.data || [],
      shifts: shiftsRes.data || [],
    };
  },
);

// Delete shift mapping
export const deleteShiftMapping = createAsyncThunk(
  "shiftMapping/deleteShiftMapping",
  async (shiftMappingId) => {
    const response = await apiRequest(
      "GET",
      `AssociateShiftMapping/DeleteShiftMapping?shiftMappingId=${shiftMappingId}`,
    );
    return response.data || response;
  },
);

const shiftMappingSlice = createSlice({
  name: "shiftMapping",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedMapping: (state) => {
      state.selectedMapping = null;
    },
    clearDropdownData: (state) => {
      state.facilities = [];
      state.employees = [];
      state.shifts = [];
      state.holidays = [];
    },
    clearAvailableEmployees: (state) => {
      state.availableEmployees = [];
    },
    clearBookedShifts: (state) => {
      state.bookedShiftIds = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Dropdown Data
      .addCase(fetchDropdownData.pending, (state) => {
        state.dropdownLoading = true;
        state.error = null;
      })
      .addCase(fetchDropdownData.fulfilled, (state, action) => {
        state.dropdownLoading = false;
        const { data, term, mappingId } = action.payload;

        // For edit mode (when data has selected array)
        if (mappingId > 0 && data && data.selected) {
          state.selectedMapping = data.selected[0] || null;
          state.facilities = data.facilities || [];
          state.employees = data.employees || [];
          state.shifts = data.shifts || [];
          state.holidays = data.holiday || [];
        } else if (mappingId === 0 && Array.isArray(data)) {
          if (term === "FACILITIES") {
            state.facilities = data;
            // Update stats with facility count
            state.stats.totalFacilities = data.length;
          } else if (term === "EMPLOYEE") {
            state.employees = data;
            // Update stats with employee count
            state.stats.totalEmployees = data.length;
          } else if (term === "SHIFT") {
            state.shifts = data;
            // Update stats with shift count
            state.stats.totalShifts = data.length;
          } else if (term === "HOLIDAY") {
            state.holidays = data;
          }
        }
      })
      .addCase(fetchDropdownData.rejected, (state, action) => {
        state.dropdownLoading = false;
        state.error = action.error?.message || "Failed to load dropdown data";
      })

      // Fetch All Dropdown Data for Stats
      .addCase(fetchAllDropdownData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllDropdownData.fulfilled, (state, action) => {
        state.loading = false;
        state.facilities = action.payload.facilities || [];
        state.employees = action.payload.employees || [];
        state.shifts = action.payload.shifts || [];
        state.stats.totalFacilities = action.payload.facilities?.length || 0;
        state.stats.totalEmployees = action.payload.employees?.length || 0;
        state.stats.totalShifts = action.payload.shifts?.length || 0;
      })
      .addCase(fetchAllDropdownData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || "Failed to load stats data";
      })

      // Fetch Booked Shifts
      .addCase(fetchBookedShifts.pending, (state) => {
        state.bookedShiftsLoading = true;
        state.bookedShiftIds = [];
      })
      .addCase(fetchBookedShifts.fulfilled, (state, action) => {
        state.bookedShiftsLoading = false;
        state.bookedShiftIds = action.payload;
      })
      .addCase(fetchBookedShifts.rejected, (state) => {
        state.bookedShiftsLoading = false;
        state.bookedShiftIds = [];
      })

      // Fetch Available Employees
      .addCase(fetchAvailableEmployees.pending, (state) => {
        state.employeesLoading = true;
        state.availableEmployees = [];
      })
      .addCase(fetchAvailableEmployees.fulfilled, (state, action) => {
        state.employeesLoading = false;
        state.availableEmployees = action.payload;
      })
      .addCase(fetchAvailableEmployees.rejected, (state) => {
        state.employeesLoading = false;
        state.availableEmployees = [];
      })

      // Fetch List
      .addCase(fetchMappingList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMappingList.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload || [];
        // Calculate unassigned associates from list
        const assignedEmployees = new Set(state.list.map(item => item.EmployeeId));
        state.stats.unassignedAssociates = state.stats.totalEmployees - assignedEmployees.size;
      })
      .addCase(fetchMappingList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || "Failed to load shift mappings";
      })
      
      // Create/Update
      .addCase(createUpdateShiftMapping.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUpdateShiftMapping.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createUpdateShiftMapping.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to save shift mapping";
      });
  },
});

export const { clearError, clearSelectedMapping, clearDropdownData, clearAvailableEmployees, clearBookedShifts } =
  shiftMappingSlice.actions;
export default shiftMappingSlice.reducer;